import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  MenuItem,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  IconButton,
  Chip,
  Switch,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  Alert,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { api } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import UserForm from "./AddUserForm";

const ROLES = ["Admin", "BDM", "Manager", "Team", "Auditor"];

const roleColor = (r) => {
  const m = {
    Admin: "error",
    BDM: "primary",
    Manager: "info",
    Team: "success",
    Auditor: "warning",
  };
  return m[r] || "default";
};

export default function Users() {
  const { can } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const [filters, setFilters] = useState({ search: "", role: "", status: "" });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
  });

  const loadUsers = async (pageNo = 1, pageSize = pagination.pageSize, f = filters) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page: pageNo,
        pageSize,
        search: f.search || "",
        role: f.role || "",
        status: f.status || "",
      }).toString();

      const res = await api.get(`/users/list.php?${query}`);
      setUsers(res.users || []);
      setPagination(
        res.pagination || { page: pageNo, pageSize, totalPages: 1, totalRecords: 0 },
      );
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Failed to load users" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1, 10, {});
    // eslint-disable-next-line
  }, []);

  const applySearch = () => loadUsers(1, pagination.pageSize, filters);

  const resetFilters = () => {
    const empty = { search: "", role: "", status: "" };
    setFilters(empty);
    loadUsers(1, pagination.pageSize, empty);
  };

  const toggleStatus = async (u) => {
    try {
      await api.post("/users/update.php", {
        id: u.id,
        is_active: u.is_active === 1 ? 0 : 1,
      });
      loadUsers(pagination.page, pagination.pageSize, filters);
    } catch (e) {
      setMsg({ type: "error", text: "Failed to update status" });
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
            <PeopleIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage staff, roles and access
            </Typography>
          </Box>
        </Stack>
        {can("users.create") && (
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => setOpenAdd(true)}
          >
            Add User
          </Button>
        )}
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search name, email or phone"
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 220 }}
          />
          <TextField
            size="small"
            select
            label="Role"
            value={filters.role}
            onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            {ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Status"
            value={filters.status}
            onChange={(e) =>
              setFilters((f) => ({ ...f, status: e.target.value }))
            }
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Active</MenuItem>
            <MenuItem value="0">Inactive</MenuItem>
          </TextField>
          <Button variant="contained" onClick={applySearch} disabled={loading}>
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={resetFilters}
            disabled={loading}
          >
            Reset
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Employee Code</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              [...Array(5)].map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell colSpan={7}>
                    <Skeleton height={36} />
                  </TableCell>
                </TableRow>
              ))}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              users.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{u.name}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{u.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{u.phone || "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {u.employee_code || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={u.role} color={roleColor(u.role)} />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={u.is_active === 1}
                      onChange={() => toggleStatus(u)}
                      disabled={!can("users.edit")}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {can("users.edit") && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setEditItem(u)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.totalRecords || 0}
          page={Math.max(0, (pagination.page || 1) - 1)}
          rowsPerPage={pagination.pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => loadUsers(p + 1, pagination.pageSize, filters)}
          onRowsPerPageChange={(e) =>
            loadUsers(1, parseInt(e.target.value, 10), filters)
          }
        />
      </Paper>

      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add User</DialogTitle>
        <DialogContent dividers>
          <UserForm
            onSuccess={() =>
              loadUsers(pagination.page, pagination.pageSize, filters)
            }
            onClose={() => setOpenAdd(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editItem}
        onClose={() => setEditItem(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          {editItem && (
            <UserForm
              data={editItem}
              onSuccess={() =>
                loadUsers(pagination.page, pagination.pageSize, filters)
              }
              onClose={() => setEditItem(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
